<template>
    <div class="min-h-screen bg-theme-canvas px-5 py-10 md:px-10">
        <div class="mx-auto max-w-2xl">
            <div class="flex items-center justify-between gap-3">
                <img
                    src="/img/logos/complete_logo.png"
                    alt="Tu Catálogo"
                    class="h-8 w-auto"
                />
                <router-link
                    to="/app"
                    class="flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-theme-border bg-theme-card px-3 py-1.5 text-sm font-medium text-theme-base no-underline transition hover:border-theme-primary"
                >
                    ← Volver a la app
                </router-link>
            </div>

            <router-link
                to="/wiki"
                class="mt-6 inline-block text-sm text-theme-primary no-underline"
            >
                ← Volver a Ayuda
            </router-link>

            <div
                v-if="isLoading"
                class="mt-10 text-center text-sm text-theme-muted"
            >
                Cargando...
            </div>

            <div v-else-if="!isAvailable" class="mt-10 text-center">
                <p class="text-lg font-semibold text-theme-base">
                    Este tema no está disponible
                </p>
                <p class="mt-2 text-sm text-theme-muted">
                    Puede que ya no exista, o que no aplique para tu tipo de
                    catálogo.
                </p>
            </div>

            <div v-else class="mt-6">
                <template v-for="(segment, i) in segments">
                    <div
                        v-if="segment.type === 'text'"
                        :key="i"
                        class="wiki-body"
                        v-html="segment.html"
                    ></div>
                    <div v-else :key="i" class="wiki-role-block wiki-body">
                        <p v-if="roleLabels.length" class="wiki-role-badge">
                            Requiere rol: {{ roleLabels.join(", ") }}
                        </p>
                        <div v-html="segment.html"></div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { WIKI_ROLE_LABELS_BY_MODE } from "@/common/fields/wiki-roles";
import { WIKI_TOPIC_SLUGS } from "@/common/fields/wiki-topics";
import {
    parseWikiFrontmatter,
    parseWikiSegments
} from "@/common/lib/wiki-markdown";

export default {
    name: "WikiTopic",
    data() {
        return {
            isLoading: true,
            isAvailable: false,
            segments: [],
            roleLabels: []
        };
    },
    watch: {
        "$route.params.slug": "loadTopic"
    },
    mounted() {
        this.loadTopic();
    },
    methods: {
        async loadTopic() {
            this.isLoading = true;
            this.isAvailable = false;

            const slug = this.$route.params.slug;
            if (!WIKI_TOPIC_SLUGS.includes(slug)) {
                this.isLoading = false;
                return;
            }

            try {
                const response = await fetch(`/wiki-content/${slug}.md`);
                if (!response.ok) {
                    this.isLoading = false;
                    return;
                }
                const raw = await response.text();
                const { body, meta } = parseWikiFrontmatter(raw);
                if (!(meta.modes || []).includes(this.$operationMode)) {
                    this.isLoading = false;
                    return;
                }
                const roleSlugs = meta.roles?.[this.$operationMode] ?? [];
                this.roleLabels = roleSlugs.map(
                    (slug) =>
                        WIKI_ROLE_LABELS_BY_MODE[this.$operationMode]?.[slug] ??
                        slug
                );
                this.segments = parseWikiSegments(body);
                this.isAvailable = true;
            } catch {
                // isAvailable queda en false
            }
            this.isLoading = false;
        }
    }
};
</script>

<style scoped>
/* El contenido de .wiki-body viene de renderMarkdown() vía v-html — no hay
   forma de aplicarle clases de Tailwind a cada tag, así que se estilan acá. */
.wiki-body >>> h1 {
    font-size: 1.875rem;
    font-weight: 600;
    color: var(--color-theme-base);
    margin-bottom: 1rem;
}

.wiki-body >>> h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-theme-base);
    margin-top: 1.75rem;
    margin-bottom: 0.75rem;
}

.wiki-body >>> p {
    color: var(--color-theme-base);
    line-height: 1.6;
    margin-bottom: 1rem;
}

.wiki-body >>> ul,
.wiki-body >>> ol {
    margin-bottom: 1rem;
    padding-left: 1.5rem;
    color: var(--color-theme-base);
}

.wiki-body >>> ul {
    list-style: disc;
}

.wiki-body >>> ol {
    list-style: decimal;
}

.wiki-body >>> li {
    margin-bottom: 0.375rem;
    line-height: 1.6;
}

.wiki-body >>> a {
    color: var(--color-theme-primary);
}

.wiki-body >>> strong {
    font-weight: 600;
}

.wiki-body >>> table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 1.5rem;
    font-size: 0.875rem;
}

.wiki-body >>> th,
.wiki-body >>> td {
    border: 1px solid var(--color-theme-border);
    padding: 0.5rem 0.75rem;
    text-align: left;
    color: var(--color-theme-base);
}

.wiki-body >>> th {
    background: var(--color-theme-hover);
    font-weight: 600;
}

.wiki-body >>> .wiki-role-block {
    border-left: 3px solid var(--color-theme-primary);
    padding-left: 1rem;
    margin: 1.25rem 0;
}

.wiki-body >>> .wiki-role-badge {
    display: inline-block;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--color-theme-primary);
    background: var(--color-theme-hover);
    border-radius: 999px;
    padding: 0.125rem 0.625rem;
    margin-bottom: 0.5rem;
}
</style>
